<template>
  <div v-if="$store.getters.getScheduleGrid.length">
    <b-container fluid class="zoom mb-5">
      <div class="schedule" aria-labelledby="schedule-heading">
        <span
          v-for="track in [
            'DNA',
            'YAYOI',
            'MAPS',
            'PACMAN',
            'TELESCOPE',
            'PIXEL',
            'MOSAIC',
            'ZOOM_OUT',
          ]"
          :key="track"
          class="track-slot"
          aria-hidden="true"
          @click="showMap(track)"
          :style="{ gridColumn: 'track-' + track, gridRow: 'tracks' }"
        >
          <img
            src="https://res.cloudinary.com/oredev/image/upload/v1716803684/2025/Icons/door.svg"
          />
          <span>{{ track }}</span>
        </span>

        <!-- Loop timeSlots in scheduleGrid, template for grid compatability -->
        <template v-for="slot in filteredSessions">
          <!-- Time label (placed in grid using timeRowLabel) -->
          <span
            class="time-slot zoom"
            :style="{ gridRow: slot.timeRowLabel }"
            :key="slot.slotStart"
          >
            {{ slot.slotStart }}
          </span>

          <!-- All sessions in this slot -->
          <div
            v-for="session in slot.sessions"
            :key="session._id"
            class="session zoom"
            :class="[
              'isServiceSession-' + session.isServiceSession,
              'isPlenumSession-' + session.isPlenumSession,
            ]"
            :style="{
              gridRow: session.timerow,
              gridColumn: session.gridColumn,
            }"
            :tabindex="session.speakerId ? 0 : null"
            :role="session.speakerId ? 'button' : null"
            @click="openModal(session.speakerId, session._id)"
            @keydown.enter="openModal(session.speakerId, session._id)"
            @keydown.space.prevent="openModal(session.speakerId, session._id)"
          >
            <span class="session-time">
              {{ session.displayTime }} &rarr; {{ session.duration }} min
            </span>
            <div class="session-meta">
              <SessionTag :tag="session.tag" />
              <RoomButton
                v-if="!session.isServiceSession"
                :room="session.roomName"
                :customClass="!session.isPlenumSession ? 'd-lg-none' : ''"
                @click="showMap(session.roomName)"
              />
            </div>
            <div class="session-title">
              <span>
                {{ session.title }}
              </span>
              <RoomButton
                v-if="session.isServiceSession"
                :room="session.roomName"
                @click="showMap(session.roomName)"
              />
            </div>
            <span class="session-speaker">
              <button
                v-if="session.speakerName"
                @click.stop="openModal(session.speakerId)"
                @keydown.enter.stop="$emit('click')"
                @keydown.space.stop.prevent="$emit('click')"
              >
                {{ session.speakerName }}
              </button>
            </span>
            <span class="session-button" v-if="!session.isPlenumSession">
              <!-- Toast -->
              <Toast
                v-if="showingToastIndex == session._id"
                :message="
                  schedule_includes(session._id)
                    ? 'Added\u00A0to\u00A0schedule'
                    : 'Removed\u00A0from\u00A0schedule'
                "
                :duration="2000"
                @hidden="showingToastIndex = null"
              />
              <button
                @click.stop="
                  schedule_edit(session._id), (showingToastIndex = session._id)
                "
                @keydown.enter.stop="$emit('click')"
                @keydown.space.stop.prevent="$emit('click')"
              >
                <span v-if="!schedule_includes(session._id)"
                  >Add<i class="material-icons" aria-hidden="true">add</i></span
                >
                <span v-else
                  >Remove<i class="material-icons" aria-hidden="true"
                    >remove</i
                  ></span
                >
              </button>
            </span>
          </div>
        </template>
      </div>
      <!-- Modals -->
      <MapModal ref="mapModal" />
      <SpeakerModal
        :speaker="selectedSpeaker"
        :sessionId="selectedSessionId"
        @closed="onModalClose"
      />
    </b-container>
  </div>
</template>

<script>
import SessionTag from "@/components/SessionTag";
import RoomButton from "@/components/RoomButton";
import Toast from "@/components/Toast";
import MapModal from "@/components/modals/MapModal";
import SpeakerModal from "@/components/modals/SpeakerModal";
import { mapMethods } from "@/mixins/helpers";
import schedule from "@/mixins/schedule";
export default {
  name: "ScheduleGrid",
  components: {
    SessionTag,
    RoomButton,
    Toast,
    MapModal,
    SpeakerModal,
  },
  mixins: [mapMethods, schedule],
  data() {
    return {
      selectedSpeaker: null,
      selectedSessionId: null,
      showingToastIndex: null,
    };
  },
  props: {
    selectedDay: Number,
  },
  computed: {
    filteredSessions() {
      const scheduleGrid = this.$store.getters.getScheduleGrid;
      return scheduleGrid[this.selectedDay - 3].timeSlots;
    },
  },
  methods: {
    getSpeakerName(session) {
      if (session.speakers.length) {
        return session.speakers[0].name;
      }
    },
    openModal(speakerId, sessionId = null) {
      const speaker = this.$store.getters.getSpeakerById(speakerId);
      if (speaker) {
        this.selectedSpeaker = speaker;
        this.selectedSessionId = sessionId;
        this.$root.$emit("bv::show::modal", "speaker-modal");
      }
    },
    onModalClose() {
      this.selectedSpeaker = null;
      this.selectedSessionId = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

// Fit schedule on laptops
.zoom {
  @media (min-width: 992px) {
    zoom: 0.7;
  }
  @media (min-width: 1100px) {
    zoom: 0.75;
  }
  @media (min-width: 1250px) {
    zoom: 0.8;
  }
  @media (min-width: 1400px) {
    zoom: 0.85;
  }
  @media (min-width: 1550px) {
    zoom: 0.9;
  }
  @media (min-width: 1720px) {
    zoom: 0.95;
  }
  @media (min-width: 1900px) {
    zoom: 1;
  }
}

/*************************
 * GRID SCHEDULE LAYOUT
 *************************/
.track-slot {
  display: none; /* hidden on small screens */
}

@media (min-width: 992px) {
  .schedule {
    display: grid;
    grid-gap: 1px;
    grid-template-rows:
      [tracks] auto
      [time-0800] auto
      [time-0805] auto
      [time-0810] auto
      [time-0815] auto
      [time-0820] auto
      [time-0825] auto
      [time-0830] auto
      [time-0835] auto
      [time-0840] auto
      [time-0845] auto
      [time-0850] auto
      [time-0855] auto
      [time-0900] auto
      [time-0905] auto
      [time-0910] auto
      [time-0915] auto
      [time-0920] auto
      [time-0925] auto
      [time-0930] auto
      [time-0935] auto
      [time-0940] auto
      [time-0945] auto
      [time-0950] auto
      [time-0955] auto
      [time-1000] auto
      [time-1005] auto
      [time-1010] auto
      [time-1015] auto
      [time-1020] auto
      [time-1025] auto
      [time-1030] auto
      [time-1035] auto
      [time-1040] auto
      [time-1045] auto
      [time-1050] auto
      [time-1055] auto
      [time-1100] auto
      [time-1105] auto
      [time-1110] auto
      [time-1115] auto
      [time-1120] auto
      [time-1125] auto
      [time-1130] auto
      [time-1135] auto
      [time-1140] auto
      [time-1145] auto
      [time-1150] auto
      [time-1155] auto
      [time-1200] auto
      [time-1205] auto
      [time-1210] auto
      [time-1215] auto
      [time-1220] auto
      [time-1225] auto
      [time-1230] auto
      [time-1235] auto
      [time-1240] auto
      [time-1245] auto
      [time-1250] auto
      [time-1255] auto
      [time-1300] auto
      [time-1305] auto
      [time-1310] auto
      [time-1315] auto
      [time-1320] auto
      [time-1325] auto
      [time-1330] auto
      [time-1335] auto
      [time-1340] auto
      [time-1345] auto
      [time-1350] auto
      [time-1355] auto
      [time-1400] auto
      [time-1405] auto
      [time-1410] auto
      [time-1415] auto
      [time-1420] auto
      [time-1425] auto
      [time-1430] auto
      [time-1435] auto
      [time-1440] auto
      [time-1445] auto
      [time-1450] auto
      [time-1455] auto
      [time-1500] auto
      [time-1505] auto
      [time-1510] auto
      [time-1515] auto
      [time-1520] auto
      [time-1525] auto
      [time-1530] auto
      [time-1535] auto
      [time-1540] auto
      [time-1545] auto
      [time-1550] auto
      [time-1555] auto
      [time-1600] auto
      [time-1605] auto
      [time-1610] auto
      [time-1615] auto
      [time-1620] auto
      [time-1625] auto
      [time-1630] auto
      [time-1635] auto
      [time-1640] auto
      [time-1645] auto
      [time-1650] auto
      [time-1655] auto
      [time-1700] auto
      [time-1705] auto
      [time-1710] auto
      [time-1715] auto
      [time-1720] auto
      [time-1725] auto
      [time-1730] auto
      [time-1735] auto
      [time-1740] auto
      [time-1745] auto
      [time-1750] auto
      [time-1755] auto
      [time-1800] auto
      [time-1805] auto
      [time-1810] auto
      [time-1815] auto
      [time-1820] auto
      [time-1825] auto
      [time-1830] auto
      [time-1835] auto
      [time-1840] auto
      [time-1845] auto
      [time-1850] auto
      [time-1855] auto
      [time-1900] auto
      [time-1905] auto
      [time-1910] auto
      [time-1915] auto
      [time-1920] auto
      [time-1925] auto
      [time-1930] auto
      [time-1935] auto
      [time-1940] auto
      [time-1945] auto
      [time-1950] auto
      [time-1955] auto
      [time-2000] auto;
    /* Note 1:
      Use 24hr time for gridline names for simplicity

      Note 2: Use "auto" instead of "auto" for a more compact schedule where height of a slot is not proportional to the session length. Implementing a "compact" shortcode attribute might make sense for this!
      Try 0.5fr for more compact equal rows. I don't quite understand how that works :)
      */

    grid-template-columns:
      [times] 4em
      [track-DNA-start] minmax(100px, 1fr)
      [track-DNA-end track-YAYOI-start] minmax(100px, 1fr)
      [track-YAYOI-end track-MAPS-start] minmax(100px, 1fr)
      [track-MAPS-end track-PACMAN-start] minmax(100px, 1fr)
      [track-PACMAN-end track-TELESCOPE-start] minmax(100px, 1fr)
      [track-TELESCOPE-end track-PIXEL-start] minmax(100px, 1fr)
      [track-PIXEL-end track-MOSAIC-start] minmax(100px, 1fr)
      [track-MOSAIC-end track-ZOOM_OUT-start] minmax(100px, 1fr)
      [track-ZOOM_OUT-end];
  }
  .track-slot {
    display: flex;
    padding: 10px 5px 5px;
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: $white;
    cursor: pointer;
  }
  .time-slot {
    grid-column: times;
  }
  .session {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .isPlenumSession-false {
    min-height: 220px;
  }
  .isPlenumSession-true {
    align-items: center;
  }
}

.session-title,
.session-time,
.session-track,
.session-speaker,
.session-button {
  display: block;
}

.session {
  border: 1px solid $dark-brown;
  border-radius: 5px;
  margin-bottom: 8px;
  padding: 16px;
  font-size: 14px;
  color: $dark-brown;
}

.isServiceSession-false {
  background-color: $program-beige;
  .session-title {
    span {
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

span {
  font-family: Lexend, sans-serif;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.05rem;
}
.track-slot {
  align-items: center; /* Vertically center image and text */
  gap: 0.5rem; /* Optional: space between image and text */
  img {
    width: 20px;
  }
  span {
    font-size: 16px;
    font-weight: 400;
    text-transform: capitalize;
  }
}
.time-slot {
  font-size: 12px;
  font-weight: 500;
}
.session-time {
  font-size: 16px;
  font-weight: 300;
  margin-bottom: 8px;
}
.session-meta {
  display: flex;
  flex-wrap: wrap; /* optional: wraps on small screens */
  margin-bottom: 8px;
  gap: 8px;
}
.session-title {
  display: flex;
  align-items: center; /* vertically align the span and button */
  gap: 8px; /* space between title and button */
  span {
    font-size: 16px;
    font-weight: 500;
  }
}
.session-speaker button {
  font-size: 14px;
  font-weight: 400;

  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  margin-bottom: 8px;
  color: $dark-brown !important; // Override IOS color

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    outline: none;
  }
}

.session-button {
  margin-top: auto;
  display: flex; /* Horizontal layout */
  align-items: center; /* Vertically center image + text */
  justify-content: center;
  position: relative;

  .toast {
    top: -80% !important; /* Show above the button */
    right: 50% !important;
    transform: translateX(50%) !important; // Center toast
  }

  button {
    padding: 6px 10px;
    border-radius: 5px;
    background: none;
    border: none;
    display: flex; /* Horizontal layout */
    align-items: center; /* Vertically center image + text */
    &:hover,
    &:focus-visible {
      outline: none;
      background: #0000001a;
    }

    span {
      color: $link-blue;
      font-weight: 700;
      font-size: 16px;
      letter-spacing: 0;
      line-height: 1.4;
      .material-icons {
        font-size: 24px;
        vertical-align: bottom; //this and line-height on span aligns it
      }
    }
  }
}

.door-icon {
  font-size: 20px;
  vertical-align: middle;
}
</style>


// WEBPACK FOOTER //
// src/components/ScheduleGrid.vue